/**
 * useLivePrice
 *
 * Checks token availability then opens a WebSocket subscription for live price
 * directly to the exchange. One server request for availability, then a
 * direct browser WebSocket to the selected exchange.
 *
 * Performance:
 * - Last prices are kept in an in-memory cache (~2m TTL, refreshed on each tick)
 *   so navigating away and back shows the last price immediately while the stream
 *   reconnects.
 * - Subscriptions are multiplexed per exchange/symbol/stream and kept warm briefly
 *   after the last consumer unmounts to avoid redundant reconnects on fast navigation.
 *
 * Exchange selection:
 *   - When `exchange` is provided → use that exchange only.
 *   - When omitted → check all exchanges in priority order and subscribe to
 *     the first one where the token is available.
 *
 * @param symbol       Token symbol, e.g. "BTC", "ETHUSDT"
 * @param exchange     Optional — pin to a specific exchange
 * @param futuresOnly  When true (default), subscribe to futures streams only.
 *                     When false, fall back to spot if token has no futures contract.
 */

import { useEffect, useReducer, useRef } from "react";

import { getClientMarketDataService } from "@/lib/market-data/market-data-service";
import {
  readDynamicLivePriceCache,
  readPinnedLivePriceCache,
} from "@/lib/live-pricing/live-price-cache";
import {
  getLivePriceStreamKey,
  subscribeSharedLivePrice,
} from "@/lib/live-pricing/live-price-subscription-registry";
import type {
  ExchangeCode,
  TokenAvailability,
} from "@/lib/market-data/market-data.types";
import type { PriceUpdate } from "@/lib/live-pricing/live-pricing.types";
import { ensureUsdtSymbol } from "@/utils/symbols";

export interface UseLivePriceResult {
  /** Latest price tick received from the exchange WS. */
  price: PriceUpdate | null;
  /** Availability from the winning exchange (cached in localStorage). */
  availability: TokenAvailability | null;
  /** True while availability is being checked across exchanges. */
  isLoading: boolean;
  /**
   * True once the token is confirmed available but the WebSocket hasn't
   * delivered the first price tick yet (connecting / subscribing phase).
   */
  isConnecting: boolean;
  /** Human-readable error message, or null if everything is healthy. */
  error: string | null;
}

interface LivePriceState {
  price: PriceUpdate | null;
  availability: TokenAvailability | null;
  isLoading: boolean;
  error: string | null;
}

type LivePriceAction =
  | { type: "RESET" }
  | { type: "LOADING" }
  | {
      type: "HYDRATE";
      payload: { availability: TokenAvailability; price: PriceUpdate };
    }
  | { type: "AVAILABLE"; payload: TokenAvailability }
  | { type: "NOT_AVAILABLE"; payload: TokenAvailability }
  | { type: "PRICE"; payload: PriceUpdate }
  | { type: "ERROR"; payload: string };

const INITIAL_STATE: LivePriceState = {
  price: null,
  availability: null,
  isLoading: false,
  error: null,
};

function reducer(
  state: LivePriceState,
  action: LivePriceAction
): LivePriceState {
  switch (action.type) {
    case "RESET":
      return INITIAL_STATE;
    case "LOADING":
      return { price: null, availability: null, isLoading: true, error: null };
    case "HYDRATE":
      return {
        ...state,
        price: action.payload.price,
        availability: action.payload.availability,
        isLoading: false,
        error: null,
      };
    case "AVAILABLE":
      return {
        ...state,
        isLoading: false,
        availability: action.payload,
        error: null,
      };
    case "NOT_AVAILABLE":
      return {
        ...state,
        isLoading: false,
        availability: action.payload,
        price: null,
      };
    case "PRICE":
      return { ...state, price: action.payload, error: null };
    case "ERROR":
      return { ...state, error: action.payload, isLoading: false };
  }
}

const EXCHANGE_PRIORITY: ExchangeCode[] = [
  "binance",
  "bybit",
  "gate",
  "kucoin",
  "hyperliquid",
  "asterdex",
];

export function useLivePrice(
  symbol: string,
  exchange?: ExchangeCode,
  futuresOnly = true,
  enabled = true
): UseLivePriceResult {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const subscriptionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !symbol) {
      dispatch({ type: "RESET" });
      return;
    }

    const symbolNorm = ensureUsdtSymbol(symbol);
    if (!symbolNorm) {
      dispatch({ type: "RESET" });
      return;
    }

    let cancelled = false;

    const cachedPinned = exchange
      ? readPinnedLivePriceCache(exchange, symbolNorm, futuresOnly)
      : null;
    const cachedDynamic = exchange
      ? null
      : readDynamicLivePriceCache(symbolNorm, futuresOnly);

    if (cachedPinned) {
      dispatch({
        type: "HYDRATE",
        payload: {
          availability: cachedPinned.availability,
          price: cachedPinned.price,
        },
      });
    } else if (cachedDynamic) {
      dispatch({
        type: "HYDRATE",
        payload: {
          availability: cachedDynamic.availability,
          price: cachedDynamic.price,
        },
      });
    } else {
      dispatch({ type: "LOADING" });
    }

    const marketData = getClientMarketDataService();

    function canConnect(avail: TokenAvailability): boolean {
      return avail.available || (!futuresOnly && Boolean(avail.spotAvailable));
    }

    function attachSubscription(
      targetExchange: ExchangeCode,
      avail: TokenAvailability
    ): void {
      const nextKey = getLivePriceStreamKey(
        targetExchange,
        symbolNorm,
        futuresOnly,
        avail
      );
      if (
        subscriptionKeyRef.current === nextKey &&
        unsubscribeRef.current
      ) {
        return;
      }

      subscriptionKeyRef.current = nextKey;
      unsubscribeRef.current?.();
      unsubscribeRef.current = subscribeSharedLivePrice(
        targetExchange,
        symbolNorm,
        avail,
        futuresOnly,
        {
          onPrice: (update) => {
            if (!cancelled) dispatch({ type: "PRICE", payload: update });
          },
          onError: (err) => {
            if (!cancelled) dispatch({ type: "ERROR", payload: err.message });
          },
        }
      );
    }

    if (exchange && cachedPinned && canConnect(cachedPinned.availability)) {
      attachSubscription(exchange, cachedPinned.availability);
    } else if (
      !exchange &&
      cachedDynamic &&
      canConnect(cachedDynamic.availability)
    ) {
      attachSubscription(cachedDynamic.exchange, cachedDynamic.availability);
    }

    if (exchange) {
      marketData
        .checkTokenAvailable(exchange, symbolNorm)
        .then((avail) => {
          if (cancelled) return;
          if (canConnect(avail)) {
            dispatch({ type: "AVAILABLE", payload: avail });
            attachSubscription(exchange, avail);
          } else {
            dispatch({ type: "NOT_AVAILABLE", payload: avail });
          }
        })
        .catch((err: unknown) => {
          if (!cancelled)
            dispatch({
              type: "ERROR",
              payload:
                err instanceof Error
                  ? err.message
                  : "Availability check failed",
            });
        });
    } else {
      marketData
        .checkTokenAvailableAll(symbolNorm)
        .then((map) => {
          if (cancelled) return;

          for (const ex of EXCHANGE_PRIORITY) {
            const avail = map[ex];
            if (avail && canConnect(avail)) {
              dispatch({ type: "AVAILABLE", payload: avail });
              attachSubscription(ex, avail);
              return;
            }
          }

          dispatch({
            type: "NOT_AVAILABLE",
            payload: {
              symbol: symbolNorm,
              available: false,
              status: "NOT_FOUND",
            },
          });
        })
        .catch((err: unknown) => {
          if (!cancelled)
            dispatch({
              type: "ERROR",
              payload:
                err instanceof Error
                  ? err.message
                  : "Availability check failed",
            });
        });
    }

    return () => {
      cancelled = true;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      subscriptionKeyRef.current = null;
    };
  }, [symbol, exchange, futuresOnly, enabled]);

  const connectible =
    state.availability?.available === true ||
    (!futuresOnly && Boolean(state.availability?.spotAvailable));

  const isConnecting =
    !state.isLoading &&
    connectible &&
    state.price === null &&
    state.error === null;

  return {
    price: state.price,
    availability: state.availability,
    isLoading: state.isLoading,
    isConnecting,
    error: state.error,
  };
}
